import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription, merge, of } from 'rxjs';
import { catchError, defaultIfEmpty, distinctUntilChanged, filter, map, switchMap, tap, timeout } from 'rxjs/operators';
import { getSessionFromLocalStorage } from 'src/app/utils/getSessionFromLocalStorage';
import { applyConsolePolicy } from 'src/app/utils/console-policy';
import { environment } from 'src/environments/environment';

// Module configuration
// Authenticated backend configuration URL; reuses the deployment API base without changing environments.
const FRONTEND_LOGS_CONFIG_URL = `${environment.apiUrl.replace(/\/$/, '')}/runtime-config`;
// Maximum configuration wait in milliseconds; a timeout keeps logs blocked and lets startup finish.
const FRONTEND_LOGS_CONFIG_TIMEOUT_MS = 5000;

/** Validate the one-field response without trusting strings, extra fields or property getters. */
function allowsFrontendLogs(response: unknown): boolean {
  if (response === null || typeof response !== 'object' || Array.isArray(response)) return false;
  const keys: string[] = Object.keys(response);
  return keys.length === 1 && keys[0] === 'allowFrontendLogs'
    && Object.getOwnPropertyDescriptor(response, 'allowFrontendLogs')?.value === true;
}

@Injectable({ providedIn: 'root' })
export class FrontendLoggingConfigService implements OnDestroy {
  private readonly http: HttpClient;
  private subscription?: Subscription;
  private initialization?: Promise<void>;
  private finishInitialization?: () => void;

  constructor(httpBackend: HttpBackend, private readonly router: Router) {
    // Bypass application interceptors to avoid configuration errors causing retries or business notifications.
    this.http = new HttpClient(httpBackend);
  }

  /** Load once per session, cancel stale requests on navigation, and keep startup bounded by the HTTP timeout. */
  initialize(): Promise<void> {
    if (this.initialization) return this.initialization;
    this.initialization = new Promise<void>((resolve: () => void): void => {
      this.finishInitialization = resolve;
      this.subscription = merge(of(null), this.router.events.pipe(filter(event => event instanceof NavigationEnd)))
        .pipe(
          map(() => this.currentSession()),
          distinctUntilChanged(),
          tap(() => applyConsolePolicy(false)),
          switchMap(session => session ? this.loadConfiguration(session) : of(false)),
          catchError(() => of(false))
        )
        .subscribe({
          next: (allowLogs: boolean): void => {
            applyConsolePolicy(allowLogs);
            this.finishInitialization?.();
            this.finishInitialization = undefined;
          },
          complete: (): void => this.finishInitialization?.(),
        });
    });
    return this.initialization;
  }

  /** Reuse the existing session reader and reject nonstring storage without introducing an alternative identity source. */
  private currentSession(): string | null {
    const session: unknown = getSessionFromLocalStorage();
    return typeof session === 'string' && session.trim() !== '' ? session : null;
  }

  /** Read only the authenticated boolean; failures and responses for an obsolete session stay disabled. */
  private loadConfiguration(session: string): Observable<boolean> {
    const params: HttpParams = new HttpParams().set('sessionid', session);
    return this.http.get<unknown>(FRONTEND_LOGS_CONFIG_URL, { params, withCredentials: true }).pipe(
      timeout(FRONTEND_LOGS_CONFIG_TIMEOUT_MS),
      map(response => allowsFrontendLogs(response) && this.currentSession() === session),
      catchError(() => of(false)),
      defaultIfEmpty(false)
    );
  }

  /** Cancel pending configuration and restore the closed state when the application is destroyed. */
  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    applyConsolePolicy(false);
    this.finishInitialization?.();
    this.finishInitialization = undefined;
  }
}
